import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { makePostApiCall } from "../../../shared/makeApiCall";
import { useToast } from "../../../contexts/ToastContext";

// ...existing imports
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";

export function ProductDetailsViewModel(logout, productId) {
  const navigate = useNavigate();
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [primaryCategoryOptions, setPrimaryCategoryOptions] = useState(null);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState(null);
  const [priceList, setPriceList] = useState(null);

  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getProduct = async function (search, page) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    let callResult = await makePostApiCall(`products/${productId}`, payload);
    if (callResult.success) {
      const data = callResult.data;
      const n = Number(data.unit_cost);
      const productPayload = {
        ...data,
        unit_cost: Number.isFinite(n) ? n : "",
      };
      setProduct(productPayload);

      const pc = productPayload.primary_category;
      const primaryCatId =
        pc != null && pc.value != null && pc.value !== ""
          ? Number(pc.value)
          : productPayload.category_id != null &&
              productPayload.category_id !== ""
            ? Number(productPayload.category_id)
            : NaN;

      if (Number.isFinite(primaryCatId) && primaryCatId > 0) {
        await getSubCategoriesByPrimary(primaryCatId);
      } else {
        setSubCategoryOptions([]);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);
  };

  const updateProduct = async function (values) {
    // assume `values.stock` and `values.low_stock_threshold` are numeric strings
    const parsedStock = parseInt(values.stock, 10);
    const parsedLowStock = parseInt(values.low_stock_threshold, 10);

    const payload = {
      ...values,
      stock: parsedStock,
      low_stock_threshold: parsedLowStock,
      priceList: priceList,
    };
    // Log the endpoint and payload for debugging before the API call
    try {
      const safePayload = { ...payload };
      // avoid logging large file objects if present
      if (safePayload.image1 && safePayload.image1 instanceof File) {
        safePayload.image1 = "<File: image1>";
      }
      if (safePayload.image2 && safePayload.image2 instanceof File) {
        safePayload.image2 = "<File: image2>";
      }
      console.log(
        "[updateProduct] POST",
        `updateProduct/${productId}`,
        safePayload
      );
    } catch (err) {
      console.log("[updateProduct] Error preparing payload log", err);
    }
    let callResult = await makePostApiCall(
      `updateProduct/${productId}`,
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(
        callResult.data.message || "Product updated"
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const errMsg =
        callResult.data && callResult.data.message
          ? callResult.data.message
          : "Could not update product";
      showErrorModal("Could not update product", errMsg);
    }
  };

  const createProduct = async function (values) {
    // parse numeric fields
    const parsedStock = parseInt(values.stock, 10);
    const parsedLowStock = parseInt(values.low_stock_threshold, 10);

    const payload = {
      ...values,
      stock: parsedStock,
      low_stock_threshold: parsedLowStock,
      priceList: priceList,
    };

    try {
      const safePayload = { ...payload };
      if (safePayload.image1 && safePayload.image1 instanceof File) {
        safePayload.image1 = "<File: image1>";
      }
      if (safePayload.image2 && safePayload.image2 instanceof File) {
        safePayload.image2 = "<File: image2>";
      }
    } catch (err) {
      console.log("[createProduct] Error preparing payload log", err);
    }

    let callResult = await makePostApiCall(
      `createProduct`,
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(
        callResult.data.message || "Product created"
      );
      window.location = `/products/${callResult.data.id}`;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const errMsg =
        callResult.data && callResult.data.message
          ? callResult.data.message
          : "Could not create product";
      showErrorModal("Could not create product", errMsg);
    }
  };

  const getPriceListForProduct = async function () {
    setLoading(true);

    let callResult = await makePostApiCall(`pricelist/${productId}`);
    setLoading(false);
    if (callResult.success) {
      setPriceList(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch product list");
    }
  };

  const getPrimaryCategoriesForDropdown = async function () {
    setLoading(true);

    let callResult = await makePostApiCall(`products/categories/dropdown`);
    setLoading(false);
    if (callResult.success) {
      setPrimaryCategoryOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch categories");
    }
  };

  const getUsersForDropdown = async function () {
    setLoading(true);

    let callResult = await makePostApiCall(`usersForDropdown`);
    setLoading(false);
    if (callResult.success) {
      setUsersOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch users");
    }
  };

  const getSubCategoriesByPrimary = async function (categoryId) {
    let callResult = await makePostApiCall(
      `/products/subcategories/dropdown/${categoryId}`
    );
    if (callResult.success) {
      setSubCategoryOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch subcategories");
    }
  };

  const deleteProductImage = async function (imageType) {
    const payload = {
      imageType: imageType,
    };
    
    try {
      const safePayload = { ...payload };
      console.log(
        "[deleteProductImage] POST",
        `delete/${productId}/${imageType}`,
        safePayload
      );
    } catch (err) {
      console.log("[deleteProductImage] Error preparing payload log", err);
    }
    
    let callResult = await makePostApiCall(
      `delete/${productId}/${imageType}`,
      payload
    );

    if (callResult.success) {
      showSuccess(
        callResult.data.message || "Image deleted"
      );
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const errMsg =
        callResult.data && callResult.data.message
          ? callResult.data.message
          : "Could not delete image";
      showErrorModal("Could not delete image", errMsg);
      return false;
    }
  };

  return {
    primaryCategoryOptions,
    getPrimaryCategoriesForDropdown,
    updateProduct,
    getProduct,
    slideOver,
    setSlideOver,
    product,
    loading,
    emptyModal,
    getUsersForDropdown,
    usersOptions,
    getSubCategoriesByPrimary,
    subCategoryOptions,
    getPriceListForProduct,
    priceList,
    setPriceList,
    createProduct,
    deleteProductImage,
  };
}
